import { Card, ConfirmDialog, PageContainer } from '@amelio/ui-library';
import { useMemo, useState } from 'react';

import { MeetingList } from './components/MeetingList';
import { MeetingsHero } from './components/MeetingsHero';
import { MeetingsToolbar } from './components/MeetingsToolbar';
import {
    CONTENT_ROW_MAX_WIDTH,
    GLASS_CONTAINER_PADDING,
    GLASS_STACK_GAP,
    PAGE_COLUMNS,
    SIDE_COLUMN_TOP_OFFSET,
} from './components/meetingStyles';
import { NewMeetingModal } from './components/NewMeetingModal/NewMeetingModal';
import { OverdueEmployeesModal } from './components/OverdueEmployeesModal';
import { QuickStats } from './components/QuickStats';
import { RecentActivity } from './components/RecentActivity';
import { useMeetingListActions } from './hooks/useMeetingListActions';
import { useNewMeetingLauncher } from './hooks/useNewMeetingLauncher';
import { toMeetingAttributeFilterParams } from './utils/meetingFilterParams';

import type { MeetingPlanTitles } from './utils/meetingTitle';
import type { ThematicMeetingListItem, ThematicMeetingTopic } from '@/types/thematic-meetings';

import { useGetCompanyInformation } from '@/api/directory';
import {
    useGetMeetingTemplates,
    useGetThematicMeetingStats,
    useGetThematicMeetings,
} from '@/api/thematic-meetings';
import { THEMATIC_MEETING_TOPIC_LABEL_KEYS } from '@/constants/ThematicMeetingTopic';
import { useAmelioTranslations } from '@/hooks';
import { useFetchErrorsHandler } from '@/hooks/api/useFetchErrorsHandler';
import { useSelectedAppLanguage } from '@/hooks/company/useSelectedAppLanguage';
import {
    defaultFiltersState,
    type SelectedFilters,
} from '@/hooks/filters/useCompanyAttributesFilters';
import { useDebounce } from '@/hooks/useDebounce';
import { useNavigate } from '@/hooks/utils/useNavigate';
import { meetingPlanPath } from '@/navigation/thematicMeetingsRoutes';
import { MEETINGS_PAGE_SIZE, RECENT_ACTIVITY_COUNT } from '@/pages/ThematicMeetings/constants';
import { hasSelectedFilters } from '@/utils/companyAttributeFilters';
import { getTextByLanguage } from '@/utils/localization';
import { lastPageOf } from '@/utils/pagination';

/**
 * "Manage your meetings" list page: upcoming / previous meetings (independently paginated),
 * quick stats, recent activity, the new-meeting creation modal, and the delete confirmation flow.
 */
export const ThematicMeetingsPage = () => {
    const { t } = useAmelioTranslations();
    const language = useSelectedAppLanguage();
    const navigate = useNavigate();

    const { data: company } = useGetCompanyInformation();

    const [searchQuery, setSearchQuery] = useState('');
    const debouncedSearch = useDebounce(searchQuery, 400);
    const [attributeFilters, setAttributeFilters] = useState<SelectedFilters>(defaultFiltersState);

    const [plannedPage, setPlannedPage] = useState(1);
    const [completedPage, setCompletedPage] = useState(1);
    // A search starts over once it settles, not at every keystroke, adjusted during the render before any
    // list is queried.
    const [pagedSearch, setPagedSearch] = useState(debouncedSearch);
    if (pagedSearch !== debouncedSearch) {
        setPagedSearch(debouncedSearch);
        setPlannedPage(1);
        setCompletedPage(1);
    }

    const [isOverdueModalOpen, setIsOverdueModalOpen] = useState(false);
    const newMeeting = useNewMeetingLauncher();
    const meetingActions = useMeetingListActions();

    // The attribute-filter half of the query: `SelectedFilters` mapped to the id-array params the
    // list endpoint contract expects, so a filter change is a server-side query, never a filter
    // applied only to the meetings already sitting in this page (the list is paginated by the
    // server, so filtering client-side would silently miss every other page's results).
    const attributeFilterParams = useMemo(
        () => toMeetingAttributeFilterParams(attributeFilters),
        [attributeFilters],
    );
    const isFiltered = hasSelectedFilters(attributeFilters) || !!debouncedSearch;

    const {
        data: plannedMeetings,
        isLoading: isPlannedLoading,
        isError: isPlannedError,
    } = useGetThematicMeetings({
        pageNumber: plannedPage,
        pageSize: MEETINGS_PAGE_SIZE,
        searchQuery: debouncedSearch || undefined,
        status: 'Planned',
        ...attributeFilterParams,
    });

    const {
        data: completedMeetings,
        isLoading: isCompletedLoading,
        isError: isCompletedError,
    } = useGetThematicMeetings({
        pageNumber: completedPage,
        pageSize: MEETINGS_PAGE_SIZE,
        searchQuery: debouncedSearch || undefined,
        status: 'Completed',
        ...attributeFilterParams,
    });

    // A list that loses the last meeting of its last page — deleted, or marked completed and so gone
    // from the upcoming list — must not leave the reader on a page that no longer exists, showing
    // nothing and offering no way back. Brought back into range during the render, like `pagedSearch`
    // above, so the page that gets queried is the corrected one.
    if (
        plannedMeetings &&
        plannedPage > lastPageOf(plannedMeetings.totalCount, MEETINGS_PAGE_SIZE)
    ) {
        setPlannedPage(lastPageOf(plannedMeetings.totalCount, MEETINGS_PAGE_SIZE));
    }
    if (
        completedMeetings &&
        completedPage > lastPageOf(completedMeetings.totalCount, MEETINGS_PAGE_SIZE)
    ) {
        setCompletedPage(lastPageOf(completedMeetings.totalCount, MEETINGS_PAGE_SIZE));
    }

    // Recent activity is "the five latest completed meetings", full stop: it must not follow the
    // "previous meetings" list into a search, a filter or page 2. Hence its own unfiltered query
    // (the backend already returns Completed sorted by completion date, newest first).
    // The panel's own title states the event it lists, and this `status` is the only thing that
    // makes that title true: widen the filter and the title has to be rewritten with it.
    const {
        data: recentlyCompletedMeetings,
        isLoading: isRecentActivityLoading,
        isError: isRecentActivityError,
    } = useGetThematicMeetings({
        pageNumber: 1,
        pageSize: RECENT_ACTIVITY_COUNT,
        status: 'Completed',
    });

    const {
        data: stats,
        isLoading: isStatsLoading,
        isError: isStatsError,
    } = useGetThematicMeetingStats();

    const {
        data: templates,
        isLoading: isTemplatesLoading,
        isError: isTemplatesError,
    } = useGetMeetingTemplates();

    useFetchErrorsHandler({
        errors: [isPlannedError, isCompletedError, isStatsError, isTemplatesError],
    });

    // A topic can have several plans, so a meeting is named after its own plan (#16660). One whose
    // plan is no longer offered, put back to draft for one, is named after its topic.
    const planTitles: MeetingPlanTitles = templates
        ? {
              byPlanId: Object.fromEntries(
                  templates.map((template) => [
                      template.id,
                      getTextByLanguage(template.title, language),
                  ]),
              ),
              byTopic: Object.fromEntries(
                  (Object.keys(THEMATIC_MEETING_TOPIC_LABEL_KEYS) as ThematicMeetingTopic[]).map(
                      (topic) => [topic, t(THEMATIC_MEETING_TOPIC_LABEL_KEYS[topic])],
                  ),
              ),
          }
        : { byPlanId: {}, byTopic: {} };

    const goToMeetingPlan = (meeting: ThematicMeetingListItem) => {
        navigate({ path: meetingPlanPath(meeting.id) });
    };

    // A new filter is a new result set: both lists start over at their first page.
    const resetPages = () => {
        setPlannedPage(1);
        setCompletedPage(1);
    };

    const handleSelectOverdueEmployee = (employeeId: string) => {
        setIsOverdueModalOpen(false);
        newMeeting.open({ employeeId });
    };

    return (
        <PageContainer>
            {/* One row, two columns, as in the design: the banner and the glass container are
                stacked in the main column, the side column sits beside both and simply starts
                lower (`SIDE_COLUMN_TOP_OFFSET`). Keeping them in one row is what guarantees the
                banner's button ends exactly where the meeting list ends — the alignment comes from
                the shared grid, not from two rows repeating the same width.
                `thematic-meetings-list-layout` lifts the ambient page cap of
                src/responsive-utilities.css, so the row's own `CONTENT_ROW_MAX_WIDTH` binds. */}
            <div
                className={`thematic-meetings-list-layout ${CONTENT_ROW_MAX_WIDTH} grid grid-cols-1 gap-6 ${PAGE_COLUMNS}`}
                data-testid={'thematic-meetings-content-row'}
            >
                <div className={'flex flex-col gap-6'}>
                    <MeetingsHero onPrepareMeeting={() => newMeeting.open()} />

                    {/* The frosted-glass container: it wraps the toolbar and the two meeting
                        sections, but not the banner above it. */}
                    <Card
                        padding={'none'}
                        borderRadius={'3xl'}
                        glass={'lg'}
                        className={GLASS_CONTAINER_PADDING}
                        dataTestId={'meetings-glass-container'}
                    >
                        <div className={`flex flex-col ${GLASS_STACK_GAP}`}>
                            <MeetingsToolbar
                                searchQuery={searchQuery}
                                onSearchChange={setSearchQuery}
                                company={company}
                                onFiltersChange={(filters) => {
                                    setAttributeFilters(filters);
                                    resetPages();
                                }}
                            />

                            <MeetingList
                                title={t('thematicMeetings.list.upcomingSection')}
                                iconName={'time-clock'}
                                meetings={plannedMeetings?.items ?? []}
                                planTitles={planTitles}
                                isLoading={isPlannedLoading}
                                isError={isPlannedError}
                                isTemplatesLoading={isTemplatesLoading}
                                emptyMessage={t(
                                    isFiltered
                                        ? 'thematicMeetings.list.noUpcomingFiltered'
                                        : 'thematicMeetings.list.noUpcoming',
                                )}
                                currentPage={plannedPage}
                                totalCount={plannedMeetings?.totalCount ?? 0}
                                onPageChange={setPlannedPage}
                                onView={goToMeetingPlan}
                                onMarkCompleted={meetingActions.markCompleted}
                                onReopen={meetingActions.reopen}
                                onDelete={meetingActions.requestDelete}
                                dataTestId={'upcoming-meetings-list'}
                            />

                            <MeetingList
                                title={t('thematicMeetings.list.previousSection')}
                                iconName={'edit-clipboard-tick'}
                                meetings={completedMeetings?.items ?? []}
                                planTitles={planTitles}
                                isLoading={isCompletedLoading}
                                // One spinner at a time: while the upcoming list loads too (a new
                                // search or filter reloads both), its spinner stands for both.
                                showsLoader={!isPlannedLoading}
                                isError={isCompletedError}
                                isTemplatesLoading={isTemplatesLoading}
                                emptyMessage={t(
                                    isFiltered
                                        ? 'thematicMeetings.list.noPreviousFiltered'
                                        : 'thematicMeetings.list.noPrevious',
                                )}
                                currentPage={completedPage}
                                totalCount={completedMeetings?.totalCount ?? 0}
                                onPageChange={setCompletedPage}
                                onView={goToMeetingPlan}
                                onMarkCompleted={meetingActions.markCompleted}
                                onReopen={meetingActions.reopen}
                                onDelete={meetingActions.requestDelete}
                                dataTestId={'previous-meetings-list'}
                            />
                        </div>
                    </Card>
                </div>

                {/* One gap at every width (see src/responsive-utilities.css). */}
                <div
                    className={`flex flex-col gap-12 ${SIDE_COLUMN_TOP_OFFSET}`}
                    data-testid={'thematic-meetings-stats-column'}
                >
                    <QuickStats
                        stats={stats}
                        isLoading={isStatsLoading}
                        onOpenOverdue={() => setIsOverdueModalOpen(true)}
                    />

                    <RecentActivity
                        meetings={recentlyCompletedMeetings?.items ?? []}
                        planTitles={planTitles}
                        isLoading={isRecentActivityLoading}
                        isError={isRecentActivityError}
                    />
                </div>
            </div>

            {/* Mounted only while open: the library `Modal` binds its Escape listener to
                `document` unconditionally and calls `preventDefault()`, so three permanently
                mounted modals would swallow every Escape press on the page. */}
            {newMeeting.isOpen && (
                <NewMeetingModal
                    onClose={newMeeting.close}
                    initialEmployee={newMeeting.prefill?.employeeId}
                    initialTopic={newMeeting.prefill?.topic}
                />
            )}

            {isOverdueModalOpen && (
                <OverdueEmployeesModal
                    isOpen
                    onClose={() => setIsOverdueModalOpen(false)}
                    onSelectEmployee={handleSelectOverdueEmployee}
                />
            )}

            {meetingActions.meetingToDelete && (
                <ConfirmDialog
                    isOpen
                    title={t('thematicMeetings.list.confirmDeleteTitle')}
                    message={t('thematicMeetings.list.confirmDeleteMessage')}
                    confirmLabel={t('thematicMeetings.list.deleteMeeting')}
                    cancelLabel={t('common.cancel')}
                    onConfirm={meetingActions.confirmDelete}
                    onCancel={meetingActions.cancelDelete}
                    isLoading={meetingActions.isDeleting}
                    isDestructive
                    dataTestId={'delete-meeting-confirm-dialog'}
                />
            )}
        </PageContainer>
    );
};
